/**
 * Migration rétrocompatible : client.billingMode + pack* → productId + productInstance
 */

function pickProduct(products, type) {
  return products.find((p) => p.type === type && p.isActive !== false) || products[0];
}

/**
 * @param {object} client
 * @param {object[]} products
 * @param {number} defaultTarif
 */
export function migrateLegacyClient(client, products, defaultTarif = 60) {
  if (!Array.isArray(products) || products.length === 0) return client;

  if (client.productId && client.productInstance) {
    return {
      ...client,
      productId: client.productId,
      productInstance: { ...client.productInstance },
    };
  }

  const sessionProd = pickProduct(products, "session");
  const packProd = pickProduct(products, "pack");
  const subProd = pickProduct(products, "subscription");
  const today = new Date().toISOString().slice(0, 10);

  const bm = client.billingMode || "session";

  if (bm === "pack" || client.pack != null || client.packSize != null) {
    const total = client.pack?.total ?? client.packSize ?? 10;
    let remaining =
      client.packRemaining ??
      (client.pack?.used != null ? total - client.pack.used : undefined);
    if (remaining == null) remaining = total;
    const used = Math.max(0, total - Number(remaining));
    return {
      ...client,
      productId: packProd?.id || sessionProd.id,
      productInstance: {
        startDate: client.pack?.purchasedAt || today,
        packAssignedAt: client.pack?.purchasedAt || `${today}T12:00:00.000Z`,
        sessionsUsed: used,
        sessionsTotal: total,
        expiresAt: null,
        status: "active",
        renewalDate: null,
      },
    };
  }

  if (bm === "subscription") {
    const cap = subProd?.sessions?.count ?? 4;
    return {
      ...client,
      productId: subProd?.id || sessionProd.id,
      productInstance: {
        startDate: today,
        sessionsUsed: 0,
        sessionsTotal: cap,
        expiresAt: null,
        status: "active",
        renewalDate: null,
      },
    };
  }

  return {
    ...client,
    productId: sessionProd.id,
    productInstance: {
      startDate: today,
      sessionsUsed: 0,
      sessionsTotal: null,
      expiresAt: null,
      status: "active",
      renewalDate: null,
    },
  };
}

export function migrateAllClients(clients, products, defaultTarif) {
  return clients.map((c) => migrateLegacyClient(c, products, defaultTarif));
}

/**
 * Nouveau client : rattache l'offre par défaut selon le mode de facturation coach (Settings).
 */
/** Libellé facturation pour grille paiements (rétrocompat billingMode). */
export function getClientBillingLabel(client, products) {
  if (!client) return "Séance";
  const product = Array.isArray(products)
    ? products.find((p) => p.id === client.productId)
    : null;
  const isPack = product?.type === "pack" || client.billingMode === "pack";
  if (isPack) {
    const total = client.productInstance?.sessionsTotal ?? client.packSize ?? null;
    const used = client.productInstance?.sessionsUsed ?? 0;
    const rem =
      client.packRemaining != null
        ? client.packRemaining
        : total != null
          ? Math.max(0, total - used)
          : null;
    return `Pack${rem != null && total != null ? ` ${rem}/${total}` : ""}`;
  }
  if (product?.type === "subscription" || client.billingMode === "subscription") {
    return "Abonnement";
  }
  if (product?.type === "programme" || client.billingMode === "programme") {
    return "Programme";
  }
  return "Séance";
}

export function isClientPackBilling(client, products) {
  const product = products?.find((p) => p.id === client?.productId);
  return product?.type === "pack" || client?.billingMode === "pack";
}

export function attachDefaultProductToClient(base, coachBillingMode, products) {
  if (!Array.isArray(products) || products.length === 0) return base;
  const type =
    coachBillingMode === "pack"
      ? "pack"
      : coachBillingMode === "subscription"
        ? "subscription"
        : coachBillingMode === "programme"
          ? "programme"
          : "session";
  const product =
    products.find((p) => p.type === type && p.isActive !== false) ||
    products.find((p) => p.type === "session");
  if (!product) return base;
  const today = new Date().toISOString().slice(0, 10);
  const cap = product.sessions?.count ?? (type === "pack" ? 10 : type === "subscription" ? 4 : null);
  const pi = {
    startDate: today,
    sessionsUsed: 0,
    sessionsTotal: type === "session" ? null : cap,
    expiresAt: null,
    status: "active",
    renewalDate: null,
  };
  if (type === "pack") {
    pi.packAssignedAt = `${today}T12:00:00.000Z`;
  }
  return {
    ...base,
    productId: product.id,
    productInstance: pi,
  };
}
